import { BadgeCheck, BellRing, KeyRound, Link2, LogOut, MailWarning } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, NOTIFICATION_CATEGORIES, NOTIFY_CATEGORY_META, type NotifyCategory } from '../api';
import { Field, Loading } from '../components';
import { currentSubscription, pushSupported, subscribeToPush } from '../push';
import { Link, useRouter } from '../router';
import { useApp } from '../store';
import { DiscordIcon, GoogleIcon } from './AuthPage';

const FUTURE_PROVIDERS = [
  { id: 'steam', label: 'Steam' },
  { id: 'playstation', label: 'PlayStation' },
  { id: 'xbox', label: 'Xbox' },
  { id: 'nintendo', label: 'Nintendo' },
  { id: 'twitch', label: 'Twitch' }
];

export function SettingsTabs({ active }: { active: 'profile' | 'notifications' | 'account' }) {
  const tabs: Array<{ key: typeof active; label: string; to: string }> = [
    { key: 'profile', label: 'Profile', to: '/profile' },
    { key: 'notifications', label: 'Notifications', to: '/settings/notifications' },
    { key: 'account', label: 'Account', to: '/settings/account' }
  ];
  return (
    <div className="tab-row" role="tablist" aria-label="Settings">
      {tabs.map((tab) => (
        <Link key={tab.key} to={tab.to} role="tab" aria-selected={active === tab.key} className={`tab ${active === tab.key ? 'active' : ''}`}>
          {tab.label}
        </Link>
      ))}
    </div>
  );
}

export function NotificationsPage() {
  const { account, authStatus, toast, refreshAuth } = useApp();
  const [deviceState, setDeviceState] = useState<'unsupported' | 'off' | 'on' | 'working'>(() => pushSupported() ? 'working' : 'unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    currentSubscription()
      .then((subscription) => setDeviceState(subscription ? 'on' : 'off'))
      .catch(() => setDeviceState('off'));
  }, []);

  if (authStatus === 'loading') return <Loading />;
  if (authStatus === 'guest') return <SignedOut />;
  if (!account) return <Loading label="Loading notification settings…" />;
  const prefs = account.notifyPrefs;

  async function enablePush() {
    setBusy(true);
    try {
      const key = await api.pushKey();
      if (!key.configured || !key.publicKey) {
        toast('Push is not configured on the server yet.', 'info');
        return;
      }
      const subscription = await subscribeToPush(key.publicKey);
      await api.savePushSubscription(subscription.toJSON());
      setDeviceState('on');
      await api.sendTestPush();
      toast('Notifications enabled — a test just went to this device.', 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not enable notifications.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function disablePush() {
    setBusy(true);
    try {
      const subscription = await currentSubscription();
      if (subscription) {
        await api.removePushSubscription(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setDeviceState('off');
      toast('Notifications disabled on this device.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not disable notifications.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function togglePref(key: NotifyCategory) {
    const next = { ...prefs, [key]: !prefs[key] };
    try {
      await api.updateProfile({ notifyPrefs: next });
      await refreshAuth();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save preferences.', 'error');
    }
  }

  return (
    <div className="page narrow">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Settings</span>
          <h1>Notifications</h1>
        </div>
      </div>
      <SettingsTabs active="notifications" />

      <section aria-label="Push delivery">
        <div className="section-heading slim"><h2>This device</h2></div>
        <div className="security-rows">
          <div className="security-row">
            <BellRing size={16} className="mint-icon" />
            <div>
              <strong>Push notifications</strong>
              <small>
                {deviceState === 'unsupported' && 'This browser does not support push notifications.'}
                {deviceState === 'working' && 'Checking…'}
                {deviceState === 'on' && 'On — you get notifications even when the tab is closed.'}
                {deviceState === 'off' && 'Enable to get notifications even when the tab is closed.'}
              </small>
            </div>
            {deviceState === 'on' && <button className="ghost small" disabled={busy} onClick={disablePush}>Disable</button>}
            {deviceState === 'off' && <button className="secondary small" disabled={busy} onClick={enablePush}>Enable</button>}
          </div>
        </div>
      </section>

      <section aria-label="What you get notified about">
        <div className="section-heading slim">
          <div>
            <h2>What you get notified about</h2>
          </div>
        </div>
        <p className="muted small-text">Each type applies to both the in-app bell and push. Turn off anything you do not want.</p>
        <div className="security-rows">
          {NOTIFICATION_CATEGORIES.map((category) => (
            <div className="security-row" key={category}>
              <div>
                <strong>{NOTIFY_CATEGORY_META[category].label}</strong>
                <small>{NOTIFY_CATEGORY_META[category].hint}</small>
              </div>
              <button
                className={`chip ${prefs[category] ? 'active' : ''}`}
                role="switch"
                aria-checked={prefs[category]}
                aria-label={`${NOTIFY_CATEGORY_META[category].label}: ${prefs[category] ? 'on' : 'off'}`}
                onClick={() => togglePref(category)}
              >
                {prefs[category] ? 'On' : 'Off'}
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AccountPage() {
  const { profile, account, providers, user, authStatus, toast, refreshAuth, logout } = useApp();
  const { navigate } = useRouter();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  if (authStatus === 'loading') return <Loading />;
  if (authStatus === 'guest') return <SignedOut />;
  if (!profile || !account) return <Loading label="Loading account…" />;

  async function resendVerification() {
    setBusy(true);
    try {
      const result = await api.requestEmailVerification();
      if (result.devLink) {
        navigate(result.devLink.replace(window.location.origin, ''));
        return;
      }
      toast(result.message, result.verified || result.emailConfigured ? 'success' : 'info');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not send the email.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function savePassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      toast(account!.hasPassword ? 'Password changed.' : 'Password set — you can now sign in with email too.', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setShowPasswordForm(false);
      await refreshAuth();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not update the password.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function unlink(provider: string, label: string) {
    if (!window.confirm(`Unlink your ${label} account?`)) return;
    setBusy(true);
    try {
      await api.unlinkAccount(provider);
      toast(`${label} unlinked.`, 'success');
      await refreshAuth();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not unlink.', 'error');
    } finally {
      setBusy(false);
    }
  }

  const loginProviders: Array<{ id: 'discord' | 'google'; label: string; icon: React.ReactNode; available: boolean }> = [
    { id: 'discord', label: 'Discord', icon: <DiscordIcon size={15} />, available: providers.discord },
    { id: 'google', label: 'Google', icon: <GoogleIcon size={15} />, available: providers.google }
  ];

  return (
    <div className="page narrow">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Settings</span>
          <h1>Account</h1>
        </div>
      </div>
      <SettingsTabs active="account" />

      <section aria-label="Sign-in and security">
        <div className="section-heading slim"><h2>Sign-in & security</h2></div>
        <div className="security-rows">
          <div className="security-row">
            {account.emailVerified ? <BadgeCheck size={16} className="mint-icon" /> : <MailWarning size={16} className="warn-icon" />}
            <div>
              <strong>{user?.email || 'No email on file'}</strong>
              <small>{account.emailVerified ? 'Email verified' : 'Not verified — verify to enable account recovery and reminders.'}</small>
            </div>
            {!account.emailVerified && user?.email && (
              <button className="secondary small" disabled={busy} onClick={resendVerification}>Verify email</button>
            )}
          </div>

          <div className="security-row">
            <KeyRound size={16} className="mint-icon" />
            <div>
              <strong>Password</strong>
              <small>{account.hasPassword ? 'Set — used for email sign-in.' : 'Not set — you sign in with a linked account.'}</small>
            </div>
            <button className="secondary small" onClick={() => setShowPasswordForm((current) => !current)}>
              {showPasswordForm ? 'Cancel' : account.hasPassword ? 'Change' : 'Set password'}
            </button>
          </div>

          {showPasswordForm && (
            <form className="stack-form password-form" onSubmit={savePassword}>
              {account.hasPassword && (
                <Field label="Current password">
                  <input value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} type="password" autoComplete="current-password" required />
                </Field>
              )}
              <Field label="New password">
                <input value={newPassword} onChange={(event) => setNewPassword(event.target.value)} type="password" minLength={8} autoComplete="new-password" required />
              </Field>
              <button className="primary small" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
            </form>
          )}

          {loginProviders.map((provider) => {
            const linked = profile.linkedProviders.includes(provider.id);
            return (
              <div className="security-row" key={provider.id}>
                {provider.icon}
                <div>
                  <strong>{provider.label}</strong>
                  <small>{linked ? 'Linked — you can sign in with it.' : provider.available ? 'Link to sign in with one tap.' : 'Available once configured.'}</small>
                </div>
                {linked ? (
                  <button className="ghost small" disabled={busy} onClick={() => unlink(provider.id, provider.label)}>Unlink</button>
                ) : provider.available ? (
                  <a className="secondary small" href={`/api/auth/oauth/${provider.id}/start`}>Link</a>
                ) : (
                  <span className="pill">Soon</span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section aria-label="Coming platform connections">
        <div className="section-heading slim">
          <div>
            <span className="eyebrow">Coming soon</span>
            <h2>Game platforms</h2>
          </div>
        </div>
        <p className="muted">Platform connections will verify who you are and pull in stats for richer game profiles.</p>
        <div className="provider-grid">
          {FUTURE_PROVIDERS.map((provider) => (
            <div key={provider.id} className="provider-card">
              <Link2 size={14} />
              <span>{provider.label}</span>
              <span className="pill">Soon</span>
            </div>
          ))}
        </div>
      </section>

      <button className="ghost danger" onClick={() => { void logout().then(() => { toast('Signed out.'); navigate('/'); }); }}>
        <LogOut size={15} /> Sign out
      </button>
    </div>
  );
}

function SignedOut() {
  return (
    <div className="page narrow">
      <h1>Sign in to manage settings</h1>
      <div className="action-bar">
        <Link to="/login" className="primary small">Sign in</Link>
        <Link to="/signup" className="secondary small">Create account</Link>
      </div>
    </div>
  );
}
